import { useEffect, useMemo, useState } from "react";
import { ArrowRight, Loader2 } from "lucide-react";
import { http } from "../../lib/api";
import { getP, money, xx } from "../../lib/casePath";
import { Card, Pill } from "../kit/Kit";
import { clienteFields, creditoFields, REVISION_GROUPS, buroFields, garFields, proyFields } from "./fields";
import { SECCIONES } from "./StepDictamen";
import { VERSION_STATUS } from "./Historial";

const fmt = (f, v) => {
  if (v === null || v === undefined || v === "") return "—";
  if (f.type === "money") return money(v);
  if (f.type === "bool") return v ? "Sí" : "No";
  if (f.type === "select") return (f.options.find((o) => o[0] === v) || [v, String(v)])[1];
  return String(v);
};
const same = (a, b) => (a ?? "") === (b ?? "") || (typeof a === "number" && typeof b === "number" && Math.abs(a - b) < 1e-9);

// Diff de palabras (LCS); si el texto es muy largo, compara por párrafo.
const diffTokens = (a, b) => {
  const split = (t) => (a.length * b.length > 1.6e7 ? t.split(/(\n+)/) : t.split(/(\s+)/)).filter((x) => x !== "");
  const A = split(a);
  const B = split(b);
  const n = A.length;
  const m = B.length;
  const L = Array.from({ length: n + 1 }, () => new Uint16Array(m + 1));
  for (let i = n - 1; i >= 0; i -= 1) for (let j = m - 1; j >= 0; j -= 1) L[i][j] = A[i] === B[j] ? L[i + 1][j + 1] + 1 : Math.max(L[i + 1][j], L[i][j + 1]);
  const out = [];
  let i = 0;
  let j = 0;
  while (i < n && j < m) {
    if (A[i] === B[j]) { out.push(["=", A[i]]); i += 1; j += 1; } else if (L[i + 1][j] >= L[i][j + 1]) { out.push(["-", A[i]]); i += 1; } else { out.push(["+", B[j]]); j += 1; }
  }
  while (i < n) { out.push(["-", A[i]]); i += 1; }
  while (j < m) { out.push(["+", B[j]]); j += 1; }
  return out;
};

const TextDiff = ({ a, b, testid }) => {
  const parts = useMemo(() => diffTokens(a || "", b || ""), [a, b]);
  return (
    <p className="whitespace-pre-wrap text-sm leading-relaxed" data-testid={testid}>
      {parts.map(([op, t], k) => (op === "=" ? <span key={k}>{t}</span>
        : op === "+" ? <span key={k} className="rounded bg-emerald-100 text-emerald-900" data-testid="diff-added">{t}</span>
          : <span key={k} className="rounded bg-rose-100 text-rose-800 line-through" data-testid="diff-removed">{t}</span>))}
    </p>
  );
};

const Delta = ({ a, b }) => {
  if (typeof a !== "number" || typeof b !== "number" || same(a, b)) return null;
  const d = b - a;
  return <span className="tabular-nums">{d > 0 ? "+" : ""}{Math.abs(d) >= 1000 ? money(d) : d.toFixed(2)}{a ? ` (${d > 0 ? "+" : ""}${((d / Math.abs(a)) * 100).toFixed(1)}%)` : ""}</span>;
};

export const Compare = ({ id, versions }) => {
  const [a, setA] = useState(versions[1]?.id);
  const [b, setB] = useState(versions[0]?.id);
  const [data, setData] = useState(null);
  const [cat, setCat] = useState(null);
  const [onlyChanges, setOnly] = useState(true);
  useEffect(() => { http.get("/motor/params").then((r) => setCat(r.data)); }, []);
  useEffect(() => { setData(null); if (a && b) http.get(`/expedientes/${id}/compare`, { params: { a, b } }).then((r) => setData(r.data)); }, [id, a, b]);

  const groups = cat ? [["Cliente", clienteFields(cat)], ["Crédito", creditoFields], ["Financieros", REVISION_GROUPS.flatMap((g) => g[1])],
    ["Buró y garantías", [...buroFields, ...garFields]], ["Proyección", proyFields]] : [];
  const sel = (v, set, tid) => (
    <select value={v} onChange={(e) => set(e.target.value)} data-testid={tid}
      className="rounded-xl border border-[#E2DDD5] bg-white px-3 py-2 text-sm font-semibold outline-none focus:border-[#E8643C]">
      {versions.map((x) => <option key={x.id} value={x.id}>v{x.version} · {VERSION_STATUS[x.status][0]}</option>)}
    </select>
  );
  const MOT = [["Score", "score"], ["Banda", "band"], ["Decisión", "rec"], ["Resuelve", "route"], ["Tasa aplicada", "rate", (v) => `${v?.toFixed(2)}%`],
    ["Monto total", "total", money], ["Monto recomendado", "montoRec", money], ["DSCR año 1", "dscr1", xx], ["DSCR año 2", "dscr2", xx]];
  const chg = "bg-[#FDF0EB]";

  return (
    <div className="space-y-6" data-testid="compare-view">
      <Card testid="compare-selector">
        <div className="flex flex-wrap items-center gap-3">
          <span className="text-sm font-semibold">Comparar</span>{sel(a, setA, "compare-select-a")}<ArrowRight size={16} className="text-[#E8643C]" />{sel(b, setB, "compare-select-b")}
          <label className="ml-auto flex items-center gap-2 text-sm"><input type="checkbox" checked={onlyChanges} onChange={(e) => setOnly(e.target.checked)} data-testid="compare-only-changes" className="accent-[#E8643C]" /> Solo cambios</label>
        </div>
      </Card>
      {!data ? <p className="flex items-center gap-2 text-sm text-[#8C867A]"><Loader2 size={15} className="animate-spin" /> Comparando…</p> : <>
        <div className="grid gap-4 md:grid-cols-2" data-testid="compare-comments">
          {[["A", data.a], ["B", data.b]].map(([k, v]) => (
            <div key={k} className="rounded-2xl border border-[#E2DDD5] bg-white p-5" data-testid={`compare-comment-${k.toLowerCase()}`}>
              <div className="flex items-center gap-2"><span className="font-serif-display text-xl font-bold">v{v.version}</span><Pill cls={VERSION_STATUS[v.status][1]}>{VERSION_STATUS[v.status][0]}</Pill></div>
              <p className="mt-2 text-sm text-[#5C5850]">{v.review ? <><b>{v.review.by?.name}:</b> {v.review.comentario || "Sin comentario"}</> : "Sin resolución del Director."}</p>
            </div>
          ))}
        </div>
        <Card title="Resultado del motor" testid="compare-motor">
          <table className="w-full text-sm"><thead><tr className="text-left text-xs uppercase tracking-widest text-[#8C867A]"><th className="py-2">Indicador</th><th>v{data.a.version}</th><th>v{data.b.version}</th><th>Diferencia</th></tr></thead>
            <tbody>{MOT.map(([l, k, f = (x) => x ?? "—"]) => {
              const c = !same(data.a.motor[k], data.b.motor[k]);
              return <tr key={k} className={`border-t border-[#F2F0EC] ${c ? chg : ""}`} data-testid={`compare-motor-${k}`}><td className="py-2 font-medium">{l}</td><td>{f(data.a.motor[k])}</td><td className={c ? "font-semibold text-[#B4472A]" : ""}>{f(data.b.motor[k])}</td><td><Delta a={data.a.motor[k]} b={data.b.motor[k]} /></td></tr>;
            })}</tbody></table>
        </Card>
        {groups.map(([g, fields]) => {
          const rows = fields.map((f) => [f, getP(data.a.case, f.k), getP(data.b.case, f.k)]).filter(([, x, y]) => !onlyChanges || !same(x, y));
          return (
            <Card key={g} title={g} subtitle={`${fields.filter((f) => !same(getP(data.a.case, f.k), getP(data.b.case, f.k))).length} cambios`} testid={`compare-group-${g}`}>
              {rows.length === 0 ? <p className="text-sm text-[#8C867A]">Sin cambios.</p> : (
                <table className="w-full text-sm"><thead><tr className="text-left text-xs uppercase tracking-widest text-[#8C867A]"><th className="py-2">Campo</th><th>v{data.a.version}</th><th>v{data.b.version}</th><th>Diferencia</th></tr></thead>
                  <tbody>{rows.map(([f, x, y]) => {
                    const c = !same(x, y);
                    return <tr key={f.k} className={`border-t border-[#F2F0EC] ${c ? chg : ""}`} data-testid={`compare-row-${f.k}`}><td className="py-2 font-medium">{f.l}</td><td>{fmt(f, x)}</td><td className={c ? "font-semibold text-[#B4472A]" : ""}>{fmt(f, y)}</td><td><Delta a={x} b={y} /></td></tr>;
                  })}</tbody></table>)}
            </Card>
          );
        })}
        <Card title="Dictamen" subtitle="Agregado en verde · eliminado en rojo tachado" testid="compare-dictamen">
          <div className="space-y-6">{SECCIONES.map(([k, l]) => {
            const x = data.a.secciones[k] || "";
            const y = data.b.secciones[k] || "";
            if (onlyChanges && x === y) return null;
            return <div key={k} data-testid={`compare-dictamen-${k}`}><p className="mb-1.5 flex items-center gap-2 text-xs font-semibold uppercase tracking-widest text-[#8C867A]">{l}{x !== y && <span className="h-2 w-2 rounded-full bg-[#E8643C]" />}</p><TextDiff a={x} b={y} testid={`compare-diff-${k}`} /></div>;
          })}</div>
        </Card>
      </>}
    </div>
  );
};
